import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {InlineFramePage} from '@models/sidebar/response/inline-frame-page';
import {shareReplay} from 'rxjs/operators';
import {PageItem} from '@models/sidebar/page-item';
import {PAGE_CONTAINER_TYPE} from '@constants/sidebar/page-type.constant';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {UpdateUrlService} from '@services/update-url/update-url.service';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';

@Injectable({
  providedIn: 'root'
})
export class FramePageService {

  private CREATE_FRAME_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/inlineFramePage';
  private UPDATE_FRAME_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/inlineFramePage/update/baseId/:itemId';
  private DELETE_FRAME_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/inlineFramePage/:tabId/baseId/:itemId/:isParentMachineGroup';
  private GET_FRAME_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/inlineFramePage/:tabId/:isParentMachineGroup';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';
  private readonly successUpdatingPage = '';

  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private favoriteNotificationService: FavoriteNotificationService,
              private updateUrlService: UpdateUrlService) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating inline frame page.');
    this.successCreatingPage = this.translateService.instant('Inline frame page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming inline frame page.');
    this.successRenamingPage = this.translateService.instant('Inline frame page was renamed.');
    this.successUpdatingPage = this.translateService.instant('Inline frame page was updated.');
  }

  public create(baseMenuItems: PageItem[], pageItem: PageItem, machineOrGroupId: string, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean, isMachineTab: boolean): void {
    if (parentId && pageItem.url) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const id = this.leftMenuService.getHighestPageIdNumber(baseMenuItems, pageItem);
      const pageId = PAGE_CONTAINER_TYPE.FRAME_PAGE + id;
      pageItem.pageId = pageId;
      const inlineFramePage = new InlineFramePage(pageId, pageItem.name, isMachineTab, pageItem.url);
      this.saveFramePage(machineOrGroupId, inlineFramePage).subscribe({
        next: (newBaseMenuItem: BaseMenuItem) => {
          this.handleCreatedPage(newBaseMenuItem, pageItem, baseMenuItems, currentPageName, parentId, isAdminOrHigher);
        }, error: (error) => {
          this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
          this.loadingIndicator.hide();
        }
      });
    }
  }

  private saveFramePage(machineId: string, request: InlineFramePage): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_FRAME_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineId);
    return this.http.post<InlineFramePage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], currentPageName: string, parentId: string, isAdminOrHigher: boolean) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  public update(pageItem: PageItem, machineOrGroupId: string, parentId: string | null, currentPageName: string, isMachineTab: boolean, nameFallback: Object): void {
    if (parentId && pageItem.id && pageItem.pageId && pageItem.url) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const frameTab = new InlineFramePage(pageItem.pageId, pageItem.name, isMachineTab, pageItem.url);
      const itemId: string = pageItem.id;
      this.updateFramePage(machineOrGroupId, itemId, frameTab).subscribe({
        next: (result: BaseMenuItem) => {
          this.handleUpdatedPage(result.name, nameFallback['name'], parentId, currentPageName, itemId, pageItem);
        }, error: (error) => {
          this.leftMenuService.broadcastErrorUpdatePageChange(nameFallback);
          this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
          this.loadingIndicator.hide();
        }
      });
    }
  }

  private updateFramePage(machineId: string, itemId: string, request: InlineFramePage): Observable<any> {
    let url: string = this.BASE_URL + this.UPDATE_FRAME_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineId)
    .replace(':itemId', itemId);
    return this.http.post<InlineFramePage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleUpdatedPage(updatedName: string, previousName: string, parentId: string, currentPageName: string, itemId: string, pageItem: PageItem) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_INSIDE_PARENT, parentId, '', updatedName));
    this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, itemId, '', updatedName));
    this.updateUrlService.broadcast(pageItem);
    this.loadingIndicator.hide();
    if (updatedName !== previousName) {
      this.notificationService.success(this.successRenamingPage, currentPageName, false);
    } else {
      this.notificationService.success(this.successUpdatingPage, currentPageName, false);
    }
  }

  public deleteFramePage(machineId: string, tabId: string, itemId: string, isParentMachineGroup: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_FRAME_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', String(machineId))
    .replace(':tabId', tabId)
    .replace(':itemId', itemId)
    .replace(':isParentMachineGroup', String(isParentMachineGroup));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public getFramePage(machineId: string, isParentMachineGroup: boolean, tabId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_FRAME_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', String(machineId))
    .replace(':tabId', tabId)
    .replace(':isParentMachineGroup', String(isParentMachineGroup));

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
    });
    return response;
  }

}
